/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { ContentSwitcher, Switch, TextInput } from '@carbon/react';
import { ChatLaunch } from '@carbon/react/icons';
import React, { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useIsNewIA } from '@ibm-watson/shared/modules/ia/hooks/useIsNewIA';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { NAMESPACES, TOOLING_TYPES } from '../../../util/web-chat';
import { TabContent } from '../../common/tabbed-header/TabContent';
import PreviewWebChat from '../../preview-web-chat/PreviewWebChat';
import { links } from '../links';
import { LauncherTab as messages } from '../messages';
import { FIELDS } from '../schema';

import './Launcher.scss';

const IDS = {
  DESKTOP_LAUNCHER_TITLE: 'integration_public_launcher_config_desktop_title',
  MOBILE_LAUNCHER_TITLE: 'integration_public_launcher_config_mobile_title'
};

const TABS = {
  DESKTOP: 'desktop',
  MOBILE: 'mobile'
};

interface LauncherProps {
  tabname?: string;
  environmentId?: string;
  integrationId: string;
  readOnly?: boolean;
}

export const Launcher = ({
  environmentId,
  integrationId,
  readOnly
}: LauncherProps) => {
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { registerInputField } = useFormHelpers(formMethods);

  const webChatContainer = useRef(null);

  const desktopTitleChangeState = useState<boolean>(false);
  const mobileTitleChangeState = useState<boolean>(false);
  const [enabledTab, setEnabledTab] = useState<string | number>(TABS.DESKTOP);

  return (
    <TabContent
      ImageComponent={() => <ChatLaunch size={32} />}
      title={<FormattedMessage {...messages.title} />}
      subtitle={t(messages.subtitle)}
      learnMoreLink={
        useIsNewIA()
          ? links.learnMoreLauncherLinkNewIA
          : links.learnMoreLauncherLinkClassic
      }>
      <div className="Launcher__control">
        <ContentSwitcher
          selectedIndex={0}
          size="md"
          className="Launcher__contentSwitcher"
          onChange={({ name }) => setEnabledTab(name)}>
          <Switch name={TABS.DESKTOP} text={t(messages.desktopTabLabel)} />
          <Switch name={TABS.MOBILE} text={t(messages.mobileTabLabel)} />
        </ContentSwitcher>
        {enabledTab === TABS.DESKTOP && (
          <div className="TabContent__field-container TabContent__field-container--full-width">
            <h4 className="TabContent__inner-title TabContent__inner-title-heading">
              <FormattedMessage {...messages.desktopLauncherTitle} />
            </h4>
            <p className="TabContent__inner-field TabContent__inner-field-body">
              <FormattedMessage {...messages.desktopLauncherDescription} />
            </p>
            <TextInput
              {...registerInputField({
                field: FIELDS.LAUNCHER_DESKTOP_TITLE,
                labelText: messages.desktopLauncherLabel,
                placeholderText: messages.desktopLauncherPlaceholder,
                changeState: desktopTitleChangeState,
                readOnly
              })}
              id={IDS.DESKTOP_LAUNCHER_TITLE}
              className="TabContent__field"
            />
            <div className="Launcher__webchat">
              <PreviewWebChat
                integrationId={integrationId}
                containerRef={webChatContainer}
                environmentId={environmentId}
                namespace={NAMESPACES.CHANNEL_PAGE}
                removeDefaultFocus
                toolingType={TOOLING_TYPES.WEB_CHAT}
                useDraftConfig
                isDesktopLauncherPreview
                disableSessionHistory
              />
              <div className="container" ref={webChatContainer} />
            </div>
          </div>
        )}
        {enabledTab === TABS.MOBILE && (
          <div className="TabContent__field-container TabContent__field-container--full-width">
            <h4 className="TabContent__inner-title TabContent__inner-title-heading">
              <FormattedMessage {...messages.mobileLauncherTitle} />
            </h4>
            <p className="TabContent__inner-field TabContent__inner-field-body">
              <FormattedMessage {...messages.mobileLauncherDescription} />
            </p>
            <TextInput
              {...registerInputField({
                field: FIELDS.LAUNCHER_MOBILE_TITLE,
                labelText: messages.mobileLauncherLabel,
                placeholderText: messages.mobileLauncherPlaceholder,
                changeState: mobileTitleChangeState,
                readOnly
              })}
              id={IDS.MOBILE_LAUNCHER_TITLE}
              className="TabContent__field"
            />
            <div className="Launcher__webchat">
              <PreviewWebChat
                integrationId={integrationId}
                containerRef={webChatContainer}
                environmentId={environmentId}
                namespace={NAMESPACES.CHANNEL_PAGE}
                removeDefaultFocus
                toolingType={TOOLING_TYPES.WEB_CHAT}
                useDraftConfig
                isMobileLauncherPreview={true}
                disableSessionHistory
              />
              <div className="container" ref={webChatContainer} />
            </div>
          </div>
        )}
      </div>
    </TabContent>
  );
};
